import { Type, Transform } from 'class-transformer';
import {
  IsInt,
  IsNumber,
  IsIn,
  IsArray,
  IsString,
  IsDateString,
  IsUUID,
  IsNotEmpty,
  ValidateNested,
  IsOptional,
  IsBoolean,
} from 'class-validator';
import { injectable } from 'inversify';

import { HttpService } from '@vk-hr-tek/core/http';
import {
  FiltersResponse,
  Filter,
  FilterService,
  FilterType,
} from '@vk-hr-tek/core/filter';
import { ValidationService } from '@vk-hr-tek/core/validation';
import { UnmarshallerService } from '@vk-hr-tek/core/unmarshaller';
import { QueryService } from '@vk-hr-tek/core/query';

export class GetCompanyPositionsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetTimezoneEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyTimezonesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetTimezoneExceptionsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetAvailableCompanyTimezoneFiltersParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyCeoParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyCeoParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class SetCompanySmevValidationEsiaParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanySmevValidationEsiaParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class SetCompanySmevValidationErrorsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanySmevValidationErrorsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateEventTypeParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class CompanyEventTypesCollectionParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class CompanyEventTypesCollectionOptionsParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class UpdateCompanyProfilesSettingsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyProfilesSettingsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetNodeOptionsParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class UpdateNodesParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class GetNewWorkflowParticipantOptionsParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class DeleteWorkflowParticipantParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class AddWorkflowParticipantParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class DeleteValidatorParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class UpdateValidatorParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class GetEventTypeWorkflowParams {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;
}

export class GetUnitStaffParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class DeleteUnitParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class UpdateUnitParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class GetUnitParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class CreateUnitParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetUnitChildrenParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class GetUnitChildrenForBatchParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class GetUnitsTreeParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetUnitsTreeForBatchParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetUnitTypesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanySubstituteEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetCompanyEmployeesWithoutRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyEmployeesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyEventTypesParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyGroupUsersParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyLogoByIdParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyLogoParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyEventsStatParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class CreateCompanyGroupParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyGroupsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class CheckCompanyGroupCreationParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyManagerBlocksParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class ToggleCompanySkipAccountSyncParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanySettingsParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompaniesFromAccountParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyRoleModelParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class UpdateCompanyParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetCompanyParams {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;
}

export class GetTimezoneFilterOptionsQuery {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetProfilesOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetProfilesQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetManagerSubstituteAbsenceOptionsQuery {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetManagerSubstitutesQuery {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsUUID()
  @IsNotEmpty()
  employee_id: string;
}

export class GetUnitStaffQuery {
  @IsUUID()
  @IsOptional()
  first_employee_id?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export type UnitType = string;

export class GetUnitsTreeQuery {
  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @IsBoolean()
  @IsOptional()
  vacation_data?: boolean;
}

export class GetUnitsTreeForBatchQuery {
  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsUUID()
  @IsOptional()
  unit_id?: string;
}

export class GetSegmentsFilterOptionsQuery {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetAbsenceTypesOptionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsString()
  @IsOptional()
  query?: string;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetUnitsOptionsQuery {
  @IsUUID()
  @IsOptional()
  company_id?: string;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  id?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsBoolean()
  @IsOptional()
  query_include_root?: boolean;

  @IsNumber()
  @IsOptional()
  limit?: number;

  @IsNumber()
  @IsOptional()
  offset?: number;
}

export class GetCompanySubstituteEmployeesQuery {
  @IsString()
  @IsOptional()
  query?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetCompanyEmployeesWithoutRoleModelQuery {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsString()
  @IsOptional()
  query?: string;

  @IsInt()
  @IsOptional()
  offset?: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];
}

export type ManagerType = string;

export class GetCompanyEventTypesQuery {
  @IsBoolean()
  @IsOptional()
  has_manager?: boolean;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  manager_type?: 'operational' | 'legal';
}

export class GetCompanyLogoQuery {
  @IsIn(['main', 'compact'])
  @IsString()
  @IsOptional()
  version?: 'main' | 'compact';

  @IsIn(['strict', 'fallback'])
  @IsString()
  @IsOptional()
  mode?: 'strict' | 'fallback';
}

export class GetCompanyLogoByIdQuery {
  @IsIn(['main', 'compact'])
  @IsString()
  @IsOptional()
  version?: 'main' | 'compact';

  @IsIn(['strict', 'fallback'])
  @IsString()
  @IsOptional()
  mode?: 'strict' | 'fallback';
}

export class GetCompanyGroupsQuery {
  @IsBoolean()
  @IsOptional()
  include_all?: boolean;

  @IsString()
  @IsOptional()
  group_name?: string;
}

export class FilterSelectBadge {
  @IsString()
  @IsNotEmpty()
  title: string;

  @IsString()
  @IsOptional()
  description?: string;

  @IsIn(['blue', 'velvet', 'red'])
  @IsString()
  @IsOptional()
  color?: 'blue' | 'velvet' | 'red';

  @IsIn(['left', 'bottom'])
  @IsString()
  @IsOptional()
  position?: 'left' | 'bottom';
}

export class FilterSelectOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @IsIn(['item'])
  @IsString()
  @IsOptional()
  type?: 'item';

  @ValidateNested()
  @Type(() => FilterSelectBadge)
  @IsOptional()
  badge?: FilterSelectBadge;
}

export type CompanyPositionsResponse = FilterSelectOption[];

export type TimezoneEmployeeStatus = string;

export type AbsenceGroupNick = string;

export class AbsenceGroup {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn([
    'vacation',
    'maternity_leave',
    'student_leave',
    'business_trip',
    'sick_leave',
  ])
  @IsString()
  @IsNotEmpty()
  nick:
    | 'vacation'
    | 'maternity_leave'
    | 'student_leave'
    | 'business_trip'
    | 'sick_leave';
}

export class EmployeeAbsence {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @ValidateNested()
  @Type(() => AbsenceGroup)
  @IsNotEmpty()
  absence_group: AbsenceGroup;
}

export type SignType = string;

export type CryptoProPluginOpenType = string;

export class CompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  tsp_url?: string;

  @IsIn(['capi', 'cades'])
  @IsString()
  @IsOptional()
  crypto_pro_plugin_open_type?: 'capi' | 'cades';

  @IsIn(['api', 'browser_plugin'])
  @IsString()
  @IsOptional()
  hash_source?: 'api' | 'browser_plugin';
}

export class CompanyEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  email?: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsOptional()
  phone?: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsOptional()
  sign_type?:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @IsUUID()
  @IsOptional()
  expanded_unit_id?: string;

  @IsString()
  @IsOptional()
  unit_name?: string;

  @IsString()
  @IsOptional()
  unit_breadcrumbs?: string;

  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @ValidateNested({ each: true })
  @Type(() => EmployeeAbsence)
  @IsArray()
  @IsOptional()
  absences?: EmployeeAbsence[];

  @IsString()
  @IsOptional()
  org_structure_type?: string;
}

export class TimezoneEmployee {
  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsNotEmpty()
  employee: CompanyEmployee;

  @IsString()
  @IsNotEmpty()
  timezone_id: string;

  @IsString()
  @IsNotEmpty()
  timezone_name: string;

  @IsIn(['unknown', 'waiting', 'success', 'error'])
  @IsString()
  @IsNotEmpty()
  status: 'unknown' | 'waiting' | 'success' | 'error';

  @IsString()
  @IsNotEmpty()
  description: string;
}

export class TimezoneEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => TimezoneEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: TimezoneEmployee[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class CompanyTimezone {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  id: string;

  @IsInt()
  @IsNotEmpty()
  employee_count: number;
}

export class CompanyTimezonesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyTimezone)
  @IsArray()
  @IsNotEmpty()
  timezones: CompanyTimezone[];
}

export class TimezoneException {
  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsNotEmpty()
  employee: CompanyEmployee;

  @IsString()
  @IsNotEmpty()
  timezone_id: string;

  @IsString()
  @IsNotEmpty()
  timezone_name: string;
}

export class TimezoneExceptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => TimezoneException)
  @IsArray()
  @IsNotEmpty()
  exceptions: TimezoneException[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export type TimezoneOptionsResponse = FilterSelectOption[];

export class FilterMultipleSearch {
  @IsString()
  @IsNotEmpty()
  key: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['multiple-search'])
  @IsString()
  @IsNotEmpty()
  type: 'multiple-search';

  @IsString()
  @IsNotEmpty()
  url: string;

  @IsString()
  @IsNotEmpty()
  placeholder: string;

  @IsBoolean()
  @IsOptional()
  hidden?: boolean;
}

export class AvailableFilters {
  @IsArray()
  @IsNotEmpty()
  filters: FilterMultipleSearch[];
}

export class GetAccessLevelsResponse {
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  items: string[];
}

export class Cases {
  @IsString()
  @IsOptional()
  genitive?: string;

  @IsString()
  @IsOptional()
  dative?: string;

  @IsString()
  @IsOptional()
  accusative?: string;

  @IsString()
  @IsOptional()
  creative?: string;

  @IsString()
  @IsOptional()
  prepositional?: string;
}

export class GetCompanyCeoResponse {
  @IsString()
  @IsOptional()
  first_name?: string;

  @IsString()
  @IsOptional()
  last_name?: string;

  @IsString()
  @IsOptional()
  middle_name?: string;

  @IsString()
  @IsOptional()
  position?: string;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  first_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  last_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  middle_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  position_cases?: Cases;
}

export class SmevValidationEsiaResponse {
  @IsBoolean()
  @IsNotEmpty()
  ignore_smev_errors_esia: boolean;
}

export class CertificateError {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn(['smev', 'release'])
  @IsString()
  @IsOptional()
  source?: 'smev' | 'release';

  @IsString()
  @IsNotEmpty()
  code: string;

  @IsIn(['fatal', 'warning'])
  @IsString()
  @IsNotEmpty()
  level: 'fatal' | 'warning';

  @IsString()
  @IsNotEmpty()
  description: string;

  @IsBoolean()
  @IsNotEmpty()
  ignore: boolean;
}

export class SmevValidationErrorsResponse {
  @ValidateNested({ each: true })
  @Type(() => CertificateError)
  @IsArray()
  @IsNotEmpty()
  smev_errors: CertificateError[];
}

export class CollectionOption {
  @IsUUID()
  @IsNotEmpty()
  external_id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class CompanyEventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  disabled: boolean;
}

export class GetCollectionOptionsResponse {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @ValidateNested()
  @Type(() => CompanyEventTypeItem)
  @IsNotEmpty()
  event_type: CompanyEventTypeItem;

  @ValidateNested({ each: true })
  @Type(() => CollectionOption)
  @IsArray()
  @IsNotEmpty()
  collections: CollectionOption[];
}

export class UnitItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsNotEmpty()
  type: 'operational' | 'legal';
}

export type CompanyProfilesSource = string;

export class CompanyProfilesSettings {
  @IsBoolean()
  @IsNotEmpty()
  enabled: boolean;

  @IsIn(['import', 'units'])
  @IsString()
  @IsOptional()
  source?: 'import' | 'units';

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsBoolean()
  @IsOptional()
  include_separate_units?: boolean;

  @ValidateNested({ each: true })
  @Type(() => UnitItem)
  @IsArray()
  @IsOptional()
  units?: UnitItem[];

  @ValidateNested({ each: true })
  @Type(() => UnitItem)
  @IsArray()
  @IsOptional()
  separate_units?: UnitItem[];

  @IsBoolean()
  @IsOptional()
  vacation_schedule_profiles?: boolean;
}

export type CompanyProfilesOptionsResponse = FilterSelectOption[];

export class ProfileItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class CompanyProfiles {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsIn(['import', 'units'])
  @IsString()
  @IsNotEmpty()
  source: 'import' | 'units';

  @ValidateNested({ each: true })
  @Type(() => ProfileItem)
  @IsArray()
  @IsNotEmpty()
  profiles: ProfileItem[];
}

export class GetProfilesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyProfiles)
  @IsArray()
  @IsNotEmpty()
  profiles: CompanyProfiles[];
}

export class CompanyGroup {
  @IsString()
  @IsNotEmpty()
  name: string;
}

export class GetCompanyGroupListResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyGroup)
  @IsArray()
  @IsNotEmpty()
  items: CompanyGroup[];
}

export class DeadlineSettingAttribute {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class DeadlineSettingsOptions {
  @ValidateNested({ each: true })
  @Type(() => DeadlineSettingAttribute)
  @IsArray()
  @IsNotEmpty()
  attributes: DeadlineSettingAttribute[];
}

export class GroupItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class GetNodeOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => GroupItem)
  @IsArray()
  @IsNotEmpty()
  groups: GroupItem[];

  @ValidateNested()
  @Type(() => DeadlineSettingsOptions)
  @IsNotEmpty()
  deadline_settings: DeadlineSettingsOptions;
}

export class GetNewWorkflowParticipantOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => GroupItem)
  @IsArray()
  @IsNotEmpty()
  groups: GroupItem[];
}

export type Operation = string;

export class AvailableDaysCheckValidatorSettings {
  @IsIn(['available_days_check'])
  @IsString()
  @IsNotEmpty()
  type: 'available_days_check';

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';
}

export class AbsenceDaysCheckValidatorSettings {
  @IsIn(['absence_days_check'])
  @IsString()
  @IsNotEmpty()
  type: 'absence_days_check';

  @IsInt()
  @IsNotEmpty()
  days_check: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;
}

export class CheckValueValidatorSettings {
  @IsIn(['check_value'])
  @IsString()
  @IsNotEmpty()
  type: 'check_value';

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';

  @IsString()
  @IsNotEmpty()
  value: string;
}

export class ValuesCompareValidatorSettings {
  @IsIn(['values_compare'])
  @IsString()
  @IsNotEmpty()
  type: 'values_compare';

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';
}

export class MinHiringDayValidatorSettings {
  @IsIn(['min_hiring_day'])
  @IsString()
  @IsNotEmpty()
  type: 'min_hiring_day';

  @IsIn(['eq', 'ne', 'gt', 'lt', 'ge', 'le'])
  @IsString()
  @IsNotEmpty()
  operation: 'eq' | 'ne' | 'gt' | 'lt' | 'ge' | 'le';
}

export class DivisibleByValidatorSettings {
  @IsIn(['divisible_by'])
  @IsString()
  @IsNotEmpty()
  type: 'divisible_by';

  @IsInt()
  @IsNotEmpty()
  days_count: number;
}

export class MaxDurationValidatorSettings {
  @IsIn(['max_duration'])
  @IsString()
  @IsNotEmpty()
  type: 'max_duration';

  @IsInt()
  @IsNotEmpty()
  max_duration: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;
}

export class MinDurationValidatorSettings {
  @IsIn(['min_duration'])
  @IsString()
  @IsNotEmpty()
  type: 'min_duration';

  @IsInt()
  @IsNotEmpty()
  min_duration: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;
}

export class MaxDateValidatorSettings {
  @IsIn(['max_date'])
  @IsString()
  @IsNotEmpty()
  type: 'max_date';

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;
}

export class MinDateValidatorSettings {
  @IsIn(['min_date'])
  @IsString()
  @IsNotEmpty()
  type: 'min_date';

  @IsInt()
  @IsNotEmpty()
  days_count: number;

  @IsBoolean()
  @IsNotEmpty()
  working_days_only: boolean;
}

export type ValidatorType = string;

export class Validator {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn([
    'min_date',
    'max_date',
    'working_day',
    'weekend_exclusion',
    'min_duration',
    'max_duration',
    'no_overlap',
    'forbidden_periods',
    'same_month',
    'vacation_available',
    'divisible_by',
    'min_hiring_day',
    'values_compare',
    'check_value',
    'absence_days_check',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'min_date'
    | 'max_date'
    | 'working_day'
    | 'weekend_exclusion'
    | 'min_duration'
    | 'max_duration'
    | 'no_overlap'
    | 'forbidden_periods'
    | 'same_month'
    | 'vacation_available'
    | 'divisible_by'
    | 'min_hiring_day'
    | 'values_compare'
    | 'check_value'
    | 'absence_days_check';

  @IsString()
  @IsNotEmpty()
  description: string;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  attributes: string[];

  @IsOptional()
  settings?:
    | MinDateValidatorSettings
    | MaxDateValidatorSettings
    | MinDurationValidatorSettings
    | MaxDurationValidatorSettings
    | DivisibleByValidatorSettings
    | MinHiringDayValidatorSettings
    | ValuesCompareValidatorSettings
    | CheckValueValidatorSettings
    | AbsenceDaysCheckValidatorSettings
    | AvailableDaysCheckValidatorSettings;
}

export type ActionType = string;

export class WorkflowTransition {
  @IsUUID()
  @IsNotEmpty()
  from_node: string;

  @IsUUID()
  @IsNotEmpty()
  to_node: string;

  @IsIn([
    'upload',
    'accept',
    'decline',
    'decline_sign',
    'sign',
    'system',
    'generate_document_from_template',
    'return',
    'competency_eval',
    'competency_profile',
    'competency_fill',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_linkedtrip_attrinfo',
    'system_linkedtrip_approveinfo',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_business_trip_file_delivery',
    'system_deadline',
    'system_create_request_sw',
    'system_trip_info_sw',
    'system_item_voucher_sw',
    'system_cancel_request_sw',
  ])
  @IsString()
  @IsNotEmpty()
  action_type:
    | 'upload'
    | 'accept'
    | 'decline'
    | 'decline_sign'
    | 'sign'
    | 'system'
    | 'generate_document_from_template'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'competency_fill'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_linkedtrip_attrinfo'
    | 'system_linkedtrip_approveinfo'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_business_trip_file_delivery'
    | 'system_deadline'
    | 'system_create_request_sw'
    | 'system_trip_info_sw'
    | 'system_item_voucher_sw'
    | 'system_cancel_request_sw';
}

export class WorkflowManagerBlock {
  @IsIn(['operational', 'legal'])
  @IsString()
  @IsNotEmpty()
  manager_type: 'operational' | 'legal';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  node_ids: string[];
}

export class WorkflowParticipantAction {
  @IsIn([
    'upload',
    'accept',
    'decline',
    'decline_sign',
    'sign',
    'system',
    'generate_document_from_template',
    'return',
    'competency_eval',
    'competency_profile',
    'competency_fill',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_linkedtrip_attrinfo',
    'system_linkedtrip_approveinfo',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_business_trip_file_delivery',
    'system_deadline',
    'system_create_request_sw',
    'system_trip_info_sw',
    'system_item_voucher_sw',
    'system_cancel_request_sw',
  ])
  @IsString()
  @IsNotEmpty()
  action_type:
    | 'upload'
    | 'accept'
    | 'decline'
    | 'decline_sign'
    | 'sign'
    | 'system'
    | 'generate_document_from_template'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'competency_fill'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_linkedtrip_attrinfo'
    | 'system_linkedtrip_approveinfo'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_business_trip_file_delivery'
    | 'system_deadline'
    | 'system_create_request_sw'
    | 'system_trip_info_sw'
    | 'system_item_voucher_sw'
    | 'system_cancel_request_sw';

  @IsInt()
  @IsNotEmpty()
  count: number;
}

export class WorkflowParticipant {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  id: string[];

  @IsString()
  @IsNotEmpty()
  role: string;

  @IsString()
  @IsOptional()
  group?: string;

  @IsString()
  @IsOptional()
  employee?: string;

  @IsString()
  @IsOptional()
  levels?: string;

  @IsInt()
  @IsOptional()
  number?: number;

  @IsBoolean()
  @IsNotEmpty()
  editable: boolean;

  @ValidateNested({ each: true })
  @Type(() => WorkflowParticipantAction)
  @IsArray()
  @IsNotEmpty()
  actions: WorkflowParticipantAction[];

  @IsBoolean()
  @IsNotEmpty()
  allow_delete: boolean;
}

export type DeadlineMultiMode = string;

export type DeadlineOutcome = string;

export type TimeUnit = string;

export type DeadlineReference = string;

export class DeadlineSetting {
  @IsIn([
    'event_deadline',
    'node_active',
    'attribute',
    'created_date',
    'created_datetime',
  ])
  @IsString()
  @IsNotEmpty()
  reference:
    | 'event_deadline'
    | 'node_active'
    | 'attribute'
    | 'created_date'
    | 'created_datetime';

  @IsIn(['minute', 'hour', 'day', 'workday'])
  @IsString()
  @IsNotEmpty()
  time_unit: 'minute' | 'hour' | 'day' | 'workday';

  @IsInt()
  @IsNotEmpty()
  term: number;

  @IsBoolean()
  @IsNotEmpty()
  round_days: boolean;

  @IsIn(['to_paper', 'cancel', 'skip'])
  @IsString()
  @IsNotEmpty()
  outcome: 'to_paper' | 'cancel' | 'skip';

  @ValidateNested()
  @Type(() => DeadlineSettingAttribute)
  @IsOptional()
  attribute?: DeadlineSettingAttribute;
}

export class DeadlineSettings {
  @ValidateNested({ each: true })
  @Type(() => DeadlineSetting)
  @IsArray()
  @IsNotEmpty()
  settings: DeadlineSetting[];

  @IsIn(['min_date', 'max_date'])
  @IsString()
  @IsOptional()
  mode?: 'min_date' | 'max_date';
}

export class WorkflowNodeAction {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsOptional()
  document_type_name?: string;

  @IsIn([
    'upload',
    'accept',
    'decline',
    'decline_sign',
    'sign',
    'system',
    'generate_document_from_template',
    'return',
    'competency_eval',
    'competency_profile',
    'competency_fill',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_linkedtrip_attrinfo',
    'system_linkedtrip_approveinfo',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_business_trip_file_delivery',
    'system_deadline',
    'system_create_request_sw',
    'system_trip_info_sw',
    'system_item_voucher_sw',
    'system_cancel_request_sw',
  ])
  @IsString()
  @IsNotEmpty()
  type:
    | 'upload'
    | 'accept'
    | 'decline'
    | 'decline_sign'
    | 'sign'
    | 'system'
    | 'generate_document_from_template'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'competency_fill'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_linkedtrip_attrinfo'
    | 'system_linkedtrip_approveinfo'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_business_trip_file_delivery'
    | 'system_deadline'
    | 'system_create_request_sw'
    | 'system_trip_info_sw'
    | 'system_item_voucher_sw'
    | 'system_cancel_request_sw';

  @IsBoolean()
  @IsNotEmpty()
  is_primary: boolean;

  @IsBoolean()
  @IsNotEmpty()
  is_employee: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  roles?: string[];
}

export class WorkflowNode {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested({ each: true })
  @Type(() => WorkflowNodeAction)
  @IsArray()
  @IsNotEmpty()
  actions: WorkflowNodeAction[];

  @ValidateNested()
  @Type(() => DeadlineSettings)
  @IsOptional()
  deadline_settings?: DeadlineSettings;

  @IsBoolean()
  @IsNotEmpty()
  is_optional: boolean;
}

export class Workflow {
  @ValidateNested({ each: true })
  @Type(() => WorkflowNode)
  @IsArray()
  @IsNotEmpty()
  nodes: WorkflowNode[];

  @ValidateNested({ each: true })
  @Type(() => WorkflowParticipant)
  @IsArray()
  @IsNotEmpty()
  participants: WorkflowParticipant[];

  @ValidateNested({ each: true })
  @Type(() => WorkflowManagerBlock)
  @IsArray()
  @IsNotEmpty()
  manager_blocks: WorkflowManagerBlock[];

  @ValidateNested({ each: true })
  @Type(() => WorkflowTransition)
  @IsArray()
  @IsNotEmpty()
  transitions: WorkflowTransition[];

  @ValidateNested({ each: true })
  @Type(() => CollectionOption)
  @IsArray()
  @IsNotEmpty()
  collections: CollectionOption[];

  @ValidateNested({ each: true })
  @Type(() => Validator)
  @IsArray()
  @IsNotEmpty()
  validators: Validator[];
}

export class GetEventTypeWorkflowResponse {
  @ValidateNested()
  @Type(() => CompanyEventTypeItem)
  @IsNotEmpty()
  event_type: CompanyEventTypeItem;

  @ValidateNested()
  @Type(() => Workflow)
  @IsNotEmpty()
  workflow: Workflow;
}

export class ManagerSubstituteAbsenceOption {
  @IsString()
  @IsNotEmpty()
  type: string;

  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;
}

export class ManagerSubstituteAbsenceOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => ManagerSubstituteAbsenceOption)
  @IsArray()
  @IsNotEmpty()
  options: ManagerSubstituteAbsenceOption[];
}

export class DateRange {
  @IsDateString()
  @IsNotEmpty()
  from_date: string;

  @IsDateString()
  @IsNotEmpty()
  to_date: string;
}

export type DeleteManagerSubstituteScope = string;

export class ManagerSubstituteDeleteOption {
  @IsIn(['ALL', 'ALL_TEMPORARY', 'ALL_PERMANENT'])
  @IsString()
  @IsNotEmpty()
  scope: 'ALL' | 'ALL_TEMPORARY' | 'ALL_PERMANENT';

  @IsInt()
  @IsNotEmpty()
  event_type_count: number;

  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  event_type_names: string[];
}

export class ManagerSubstitute {
  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsNotEmpty()
  substitute: CompanyEmployee;

  @ValidateNested({ each: true })
  @Type(() => ManagerSubstituteDeleteOption)
  @IsArray()
  @IsOptional()
  available_options?: ManagerSubstituteDeleteOption[];

  @ValidateNested({ each: true })
  @Type(() => DateRange)
  @IsArray()
  @IsOptional()
  dates?: DateRange[];

  @IsBoolean()
  @IsOptional()
  from_absences?: boolean;
}

export class ManagerSubstitutesEventType {
  @ValidateNested()
  @Type(() => CompanyEventTypeItem)
  @IsNotEmpty()
  event_type: CompanyEventTypeItem;

  @ValidateNested({ each: true })
  @Type(() => ManagerSubstitute)
  @IsArray()
  @IsNotEmpty()
  substitutes: ManagerSubstitute[];
}

export class GetManagerSubstitutesResponse {
  @ValidateNested({ each: true })
  @Type(() => ManagerSubstitutesEventType)
  @IsArray()
  @IsNotEmpty()
  event_types: ManagerSubstitutesEventType[];
}

export class ManagerSubstitutesEmployee {
  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsNotEmpty()
  employee: CompanyEmployee;

  @IsInt()
  @IsNotEmpty()
  substitutes_count: number;
}

export class GetManagerSubstitutesListResponse {
  @ValidateNested({ each: true })
  @Type(() => ManagerSubstitutesEmployee)
  @IsArray()
  @IsNotEmpty()
  substitutes: ManagerSubstitutesEmployee[];

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class UnitStaffPosition {
  @IsString()
  @IsOptional()
  position_name?: string;

  @IsNumber()
  @IsOptional()
  fte_vacancy?: number;

  @IsBoolean()
  @IsNotEmpty()
  is_obsolete: boolean;
}

export class UnitStaffEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  display_number: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @IsIn(['full', 'partial', 'vacant'])
  @IsString()
  @IsOptional()
  staff_position_state?: 'full' | 'partial' | 'vacant';

  @IsInt()
  @IsOptional()
  staff_position_percent?: number;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @ValidateNested({ each: true })
  @Type(() => EmployeeAbsence)
  @IsArray()
  @IsOptional()
  absences?: EmployeeAbsence[];
}

export class UnitStaff {
  @ValidateNested()
  @Type(() => UnitStaffEmployee)
  @IsOptional()
  employee?: UnitStaffEmployee;

  @ValidateNested()
  @Type(() => UnitStaffPosition)
  @IsOptional()
  staff_position?: UnitStaffPosition;
}

export class GetUnitStaffResponse {
  @ValidateNested({ each: true })
  @Type(() => UnitStaff)
  @IsArray()
  @IsNotEmpty()
  staff: UnitStaff[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class UnitStaffStats {
  @IsNumber()
  @IsOptional()
  fte_total?: number;

  @IsNumber()
  @IsOptional()
  fte_fact?: number;

  @IsNumber()
  @IsOptional()
  fte_vacancy?: number;

  @IsNumber()
  @IsOptional()
  ptr_total?: number;

  @IsNumber()
  @IsOptional()
  ptr_vacancy?: number;
}

export type UnitManagerSettingApprove = string;

export class UnitManagerSetting {
  @ValidateNested()
  @Type(() => CompanyEventTypeItem)
  @IsNotEmpty()
  event_type: CompanyEventTypeItem;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsNotEmpty()
  approve: 'always' | 'direct_only' | 'never';
}

export class Unit {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsBoolean()
  @IsNotEmpty()
  limited_access: boolean;

  @ValidateNested()
  @Type(() => UnitStaffEmployee)
  @IsOptional()
  manager?: UnitStaffEmployee;

  @ValidateNested({ each: true })
  @Type(() => UnitManagerSetting)
  @IsArray()
  @IsOptional()
  manager_settings?: UnitManagerSetting[];

  @ValidateNested({ each: true })
  @Type(() => CompanyEmployee)
  @IsArray()
  @IsOptional()
  assistants?: CompanyEmployee[];

  @IsBoolean()
  @IsNotEmpty()
  is_separate: boolean;

  @IsBoolean()
  @IsOptional()
  has_separate_above?: boolean;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsOptional()
  manager_setting_approve?: 'always' | 'direct_only' | 'never';
}

export class UnitPathItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;
}

export class GetUnitResponse {
  @ValidateNested({ each: true })
  @Type(() => UnitPathItem)
  @IsArray()
  @IsNotEmpty()
  path: UnitPathItem[];

  @ValidateNested()
  @Type(() => Unit)
  @IsNotEmpty()
  unit: Unit;

  @ValidateNested()
  @Type(() => UnitStaffStats)
  @IsOptional()
  unit_staff_stats?: UnitStaffStats;

  @ValidateNested()
  @Type(() => UnitStaffStats)
  @IsOptional()
  combined_staff_stats?: UnitStaffStats;
}

export class CreateUnitResponse {
  @IsUUID()
  @IsNotEmpty()
  unit_id: string;
}

export class UnitNode {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @ValidateNested()
  @Type(() => CompanyEmployee)
  @IsOptional()
  manager?: CompanyEmployee;

  @IsInt()
  @IsNotEmpty()
  employee_count: number;

  @IsBoolean()
  @IsNotEmpty()
  is_parent: boolean;

  @IsBoolean()
  @IsNotEmpty()
  is_separate: boolean;

  @IsBoolean()
  @IsOptional()
  has_separate_above?: boolean;

  @IsBoolean()
  @IsOptional()
  has_vacation_schedule?: boolean;

  @ValidateNested({ each: true })
  @Type(() => UnitNode)
  @IsArray()
  @IsOptional()
  children?: UnitNode[];
}

export class GetUnitChildrenResponse {
  @ValidateNested({ each: true })
  @Type(() => UnitNode)
  @IsArray()
  @IsOptional()
  units?: UnitNode[];
}

export class UnitsPermissions {
  @IsBoolean()
  @IsNotEmpty()
  edit_units: boolean;

  @IsBoolean()
  @IsNotEmpty()
  edit_manager_settings: boolean;
}

export class GetUnitsTreeResponse {
  @IsInt()
  @IsNotEmpty()
  total: number;

  @ValidateNested()
  @Type(() => UnitNode)
  @IsOptional()
  root_unit?: UnitNode;

  @ValidateNested()
  @Type(() => UnitsPermissions)
  @IsOptional()
  permissions?: UnitsPermissions;
}

export type CompaniesOptionsResponse = FilterSelectOption[];

export type SegmentsOptionsResponse = FilterSelectOption[];

export class AbsenceFilterType {
  @IsIn(['vacation', 'sick_leave', 'remote_work', 'other'])
  @IsString()
  @IsNotEmpty()
  type: 'vacation' | 'sick_leave' | 'remote_work' | 'other';

  @IsString()
  @IsNotEmpty()
  description: string;
}

export class AbsenceFilterOption {
  @IsString()
  @IsNotEmpty()
  value: string;

  @IsString()
  @IsNotEmpty()
  label: string;

  @ValidateNested()
  @Type(() => AbsenceFilterType)
  @IsOptional()
  type?: AbsenceFilterType;
}

export type AbsencesOptionsResponse = AbsenceFilterOption[];

export type UnitsOptionsResponse = FilterSelectOption[];

export class UnitTypeItem {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsNotEmpty()
  type: 'operational' | 'legal';
}

export class GetUnitTypesResponse {
  @ValidateNested({ each: true })
  @Type(() => UnitTypeItem)
  @IsArray()
  @IsNotEmpty()
  types: UnitTypeItem[];
}

export class GetCompanyEmployeesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: CompanyEmployee[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;
}

export class GetCompanyEmployeesSubstituteOptionsResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: CompanyEmployee[];

  @IsInt()
  @IsNotEmpty()
  total_count: number;

  @IsBoolean()
  @IsNotEmpty()
  has_account: boolean;
}

export class GetCompanyEventTypesResponse {
  @ValidateNested({ each: true })
  @Type(() => CompanyEventTypeItem)
  @IsArray()
  @IsNotEmpty()
  event_types: CompanyEventTypeItem[];
}

export class GroupUserEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;
}

export class GroupUser {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @ValidateNested({ each: true })
  @Type(() => GroupUserEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: GroupUserEmployee[];

  @ValidateNested({ each: true })
  @Type(() => ProfileItem)
  @IsArray()
  @IsOptional()
  profiles?: ProfileItem[];

  @IsBoolean()
  @IsNotEmpty()
  editable: boolean;
}

export class GroupUsers {
  @ValidateNested()
  @Type(() => GroupItem)
  @IsNotEmpty()
  group: GroupItem;

  @IsBoolean()
  @IsNotEmpty()
  employee_profiles_enabled: boolean;

  @ValidateNested({ each: true })
  @Type(() => GroupUser)
  @IsArray()
  @IsNotEmpty()
  users: GroupUser[];
}

export class CompanyGroupUsers {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @ValidateNested({ each: true })
  @Type(() => GroupUsers)
  @IsArray()
  @IsNotEmpty()
  group_users: GroupUsers[];
}

export class EventStatItem {
  @IsInt()
  @IsNotEmpty()
  active: number;

  @IsInt()
  @IsNotEmpty()
  finished: number;

  @IsInt()
  @IsNotEmpty()
  canceled: number;

  @IsInt()
  @IsNotEmpty()
  in_paper: number;

  @IsInt()
  @IsNotEmpty()
  total: number;
}

export class TimeRange {
  @IsDateString()
  @IsOptional()
  start?: string;

  @IsDateString()
  @IsOptional()
  end?: string;
}

export class EventTypeItemChild_button {
  @IsBoolean()
  @IsNotEmpty()
  is_active: boolean;

  @ValidateNested({ each: true })
  @Type(() => TimeRange)
  @IsArray()
  @IsNotEmpty()
  active_periods: TimeRange[];
}

export type EventCategory = string;

export class EventTypeItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsIn(['item'])
  @IsString()
  @IsNotEmpty()
  type: 'item';

  @IsString()
  @IsNotEmpty()
  custom_type: string;

  @IsIn(['employee', 'candidate', 'vacation'])
  @IsString()
  @IsOptional()
  category?: 'employee' | 'candidate' | 'vacation';

  @IsBoolean()
  @IsOptional()
  document_info_required?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  disabled: boolean;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  tags?: string[];

  @ValidateNested()
  @Type(() => EventTypeItemChild_button)
  @IsOptional()
  child_button?: EventTypeItemChild_button;
}

export class EventStat {
  @ValidateNested()
  @Type(() => EventTypeItem)
  @IsNotEmpty()
  event_type: EventTypeItem;

  @ValidateNested()
  @Type(() => EventStatItem)
  @IsNotEmpty()
  stat: EventStatItem;

  @ValidateNested({ each: true })
  @Type(() => CollectionOption)
  @IsArray()
  @IsNotEmpty()
  collections: CollectionOption[];
}

export class CompanyEventsStat {
  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company: CompanyItem;

  @ValidateNested({ each: true })
  @Type(() => EventStat)
  @IsArray()
  @IsNotEmpty()
  events_stat: EventStat[];
}

export class CreateCompanyGroupResponseItem {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsBoolean()
  @IsNotEmpty()
  success: boolean;
}

export class CreateCompanyGroupResponse {
  @ValidateNested({ each: true })
  @Type(() => CreateCompanyGroupResponseItem)
  @IsArray()
  @IsNotEmpty()
  result: CreateCompanyGroupResponseItem[];
}

export class Group {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsNotEmpty()
  parent_id: string;
}

export class Groups {
  @ValidateNested({ each: true })
  @Type(() => Group)
  @IsArray()
  @IsNotEmpty()
  groups: Group[];
}

export class CheckCompanyGroupCreationResponseItem {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsIn(['success', 'already_exists', 'no_parent_group'])
  @IsString()
  @IsNotEmpty()
  status: 'success' | 'already_exists' | 'no_parent_group';
}

export class CheckCompanyGroupCreationResponse {
  @ValidateNested({ each: true })
  @Type(() => CheckCompanyGroupCreationResponseItem)
  @IsArray()
  @IsNotEmpty()
  result: CheckCompanyGroupCreationResponseItem[];
}

export class UpdateCompanyManagerBlocksResponse {
  @IsInt()
  @IsNotEmpty()
  updated_count: number;
}

export type CompanySyncSetting = string;

export class EmployeeIdWithSignType {
  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  sign_type:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsUUID()
  @IsNotEmpty()
  id: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  last_name: string;

  @Transform(({ value }) => 'hidden *****', { groups: ['sensitive'] })
  @IsString()
  @IsNotEmpty()
  middle_name: string;

  @IsString()
  @IsNotEmpty()
  personnel_number: string;
}

export type EmployeeSignTypeSource = string;

export class EditableCompany {
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  available_sign_types: string[];

  @IsIn(['web', 'onecvk', 'public'])
  @IsString()
  @IsNotEmpty()
  employee_sign_type_source: 'web' | 'onecvk' | 'public';

  @ValidateNested()
  @Type(() => CompanyItem)
  @IsNotEmpty()
  company_item: CompanyItem;

  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  sign_type:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @ValidateNested({ each: true })
  @Type(() => EmployeeIdWithSignType)
  @IsArray()
  @IsNotEmpty()
  user_with_sign_type: EmployeeIdWithSignType[];

  @IsBoolean()
  @IsOptional()
  import_operational_units?: boolean;

  @IsBoolean()
  @IsOptional()
  import_legal_units?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  payslips_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;

  @IsBoolean()
  @IsOptional()
  update_managers_in_events?: boolean;

  @IsInt()
  @IsNotEmpty()
  employee_limit: number;

  @IsInt()
  @IsNotEmpty()
  employee_count: number;

  @IsBoolean()
  @IsOptional()
  early_employee_event_access_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  manager_starts_from_parent_unit?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  separate_units_enabled: boolean;

  @IsBoolean()
  @IsOptional()
  employee_download_pdf_access?: boolean;

  @IsBoolean()
  @IsOptional()
  show_closest_task_widget?: boolean;

  @IsBoolean()
  @IsOptional()
  business_trips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  search_users_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  kontur_fallback_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  account_role_model_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  account_role_excluded?: boolean;

  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsString()
  @IsOptional()
  account_name?: string;

  @IsBoolean()
  @IsNotEmpty()
  account_with_one_company: boolean;

  @IsBoolean()
  @IsOptional()
  show_operational_manager?: boolean;

  @IsBoolean()
  @IsOptional()
  show_legal_manager?: boolean;

  @IsBoolean()
  @IsOptional()
  show_functional_manager?: boolean;

  @IsBoolean()
  @IsOptional()
  allow_functional?: boolean;

  @IsString()
  @IsNotEmpty()
  timezone_id: string;

  @IsString()
  @IsNotEmpty()
  timezone_name: string;

  @IsInt()
  @IsNotEmpty()
  exceptions_timezone_count: number;

  @IsIn(
    [
      'show_closest_task_widget',
      'payslips_enabled',
      'early_employee_event_access_enabled',
      'manager_starts_from_parent_unit',
      'update_managers_in_events',
      'separate_units_enabled',
      'employee_download_pdf_access',
      'import_operational_units',
      'import_legal_units',
      'business_trips_enabled',
      'search_users_enabled',
      'kontur_fallback_enabled',
    ],
    { each: true },
  )
  @IsString({ each: true })
  @IsArray()
  @IsNotEmpty()
  skip_account_sync: (
    | 'show_closest_task_widget'
    | 'payslips_enabled'
    | 'early_employee_event_access_enabled'
    | 'manager_starts_from_parent_unit'
    | 'update_managers_in_events'
    | 'separate_units_enabled'
    | 'employee_download_pdf_access'
    | 'import_operational_units'
    | 'import_legal_units'
    | 'business_trips_enabled'
    | 'search_users_enabled'
    | 'kontur_fallback_enabled'
  )[];

  @IsBoolean()
  @IsOptional()
  import_timezones?: boolean;

  @IsBoolean()
  @IsNotEmpty()
  import_timezones_available: boolean;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsOptional()
  manager_setting_approve?: 'always' | 'direct_only' | 'never';

  @IsBoolean()
  @IsOptional()
  process_catalog_available?: boolean;
}

export class OrgStructureCompanyItem {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  is_admin: boolean;
}

export class OrgStructureCompanyListResponse {
  @ValidateNested({ each: true })
  @Type(() => OrgStructureCompanyItem)
  @IsArray()
  @IsNotEmpty()
  companies: OrgStructureCompanyItem[];
}

export class CompanyListResponse {
  @ValidateNested({ each: true })
  @Type(() => EditableCompany)
  @IsArray()
  @IsNotEmpty()
  companies: EditableCompany[];
}

export class RoleModelAccessControlItemKeyValue {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  value: number;
}

export class RoleModelAccessControlItem {
  @IsBoolean()
  @IsNotEmpty()
  enabled: boolean;

  @IsIn(['employees', 'managers', 'company_groups'])
  @IsString()
  @IsNotEmpty()
  type: 'employees' | 'managers' | 'company_groups';

  @IsInt()
  @IsOptional()
  value?: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  group_ids?: string[];
}

export class RoleModelObject {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsInt()
  @IsNotEmpty()
  id: number;

  @IsString()
  @IsOptional()
  tooltip?: string;

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItem)
  @IsArray()
  @IsNotEmpty()
  access_control: RoleModelAccessControlItem[];
}

export class RoleModelDomain {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsString()
  @IsNotEmpty()
  group_name: string;

  @IsString()
  @IsOptional()
  tooltip?: string;

  @ValidateNested({ each: true })
  @Type(() => RoleModelObject)
  @IsArray()
  @IsOptional()
  objects?: RoleModelObject[];

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItemKeyValue)
  @IsArray()
  @IsNotEmpty()
  employees_values: RoleModelAccessControlItemKeyValue[];

  @ValidateNested({ each: true })
  @Type(() => RoleModelAccessControlItemKeyValue)
  @IsArray()
  @IsNotEmpty()
  managers_values: RoleModelAccessControlItemKeyValue[];
}

export class GetCompanyRoleModelResponse {
  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;

  @ValidateNested({ each: true })
  @Type(() => RoleModelDomain)
  @IsArray()
  @IsNotEmpty()
  domains: RoleModelDomain[];
}

export class GetCompanyResponse {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsBoolean()
  @IsNotEmpty()
  role_model_enabled: boolean;

  @IsBoolean()
  @IsNotEmpty()
  separate_units_enabled: boolean;

  @IsUUID()
  @IsNotEmpty()
  account_id: string;

  @IsBoolean()
  @IsOptional()
  account_with_one_company?: boolean;
}

export class GetCompanyPositionsRequestFilters {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;

  @IsString()
  @IsOptional()
  custom_type?: string;
}

export class Pagination {
  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class GetCompanyPositionsRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => GetCompanyPositionsRequestFilters)
  @IsOptional()
  filters?: GetCompanyPositionsRequestFilters;
}

export class TimezoneEmployeesFilters {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  timezone_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];
}

export class TimezoneEmployeesRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => TimezoneEmployeesFilters)
  @IsOptional()
  filters?: TimezoneEmployeesFilters;
}

export class TimezoneExceptionsFilters {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  timezone_ids?: string[];

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  employee_ids?: string[];
}

export class TimezoneExceptionsRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => TimezoneExceptionsFilters)
  @IsOptional()
  filters?: TimezoneExceptionsFilters;
}

export class EmployeeTimezone {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;

  @IsString()
  @IsOptional()
  timezone_id?: string;
}

export class UpdateEmployeesTimezoneRequest {
  @ValidateNested({ each: true })
  @Type(() => EmployeeTimezone)
  @IsArray()
  @IsNotEmpty()
  employees: EmployeeTimezone[];
}

export class UpdateCompanyCeoRequest {
  @IsString()
  @IsNotEmpty()
  first_name: string;

  @IsString()
  @IsNotEmpty()
  last_name: string;

  @IsString()
  @IsOptional()
  middle_name?: string;

  @IsString()
  @IsOptional()
  position?: string;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  first_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  last_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  middle_name_cases?: Cases;

  @ValidateNested()
  @Type(() => Cases)
  @IsOptional()
  position_cases?: Cases;
}

export class SmevValidationEsiaRequest {
  @IsBoolean()
  @IsNotEmpty()
  ignore_smev_errors_esia: boolean;

  @IsBoolean()
  @IsNotEmpty()
  apply_to_account: boolean;
}

export class SmevValidationErrorsRequestData {
  @IsUUID()
  @IsNotEmpty()
  error_id: string;

  @IsBoolean()
  @IsNotEmpty()
  ignore: boolean;
}

export class SmevValidationErrorsRequest {
  @IsBoolean()
  @IsNotEmpty()
  apply_to_account: boolean;

  @ValidateNested({ each: true })
  @Type(() => SmevValidationErrorsRequestData)
  @IsArray()
  @IsNotEmpty()
  data: SmevValidationErrorsRequestData[];
}

export class UpdateEventTypeRequest {
  @IsBoolean()
  @IsOptional()
  disabled?: boolean;
}

export class UpdateCollectionsRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  collection_ids: string[];
}

export class UpdateCompanyProfilesSettings {
  @IsBoolean()
  @IsNotEmpty()
  enabled: boolean;

  @IsBoolean()
  @IsOptional()
  reset_default_profile?: boolean;

  @IsIn(['import', 'units'])
  @IsString()
  @IsOptional()
  source?: 'import' | 'units';

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsBoolean()
  @IsOptional()
  include_separate_units?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  units?: string[];

  @IsBoolean()
  @IsOptional()
  vacation_schedule_profiles?: boolean;
}

export class DeleteUserGroupRequest {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @IsUUID()
  @IsNotEmpty()
  group_id: string;
}

export class AddUserGroupRequest {
  @IsUUID()
  @IsNotEmpty()
  user_id: string;

  @IsUUID()
  @IsNotEmpty()
  group_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  profiles?: string[];
}

export class GetCompanyGroupListRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  company_ids: string[];
}

export class UpdateNodeRoles {
  @IsIn([
    'upload',
    'accept',
    'decline',
    'decline_sign',
    'sign',
    'system',
    'generate_document_from_template',
    'return',
    'competency_eval',
    'competency_profile',
    'competency_fill',
    'system_booking_hook',
    'system_booking_approve',
    'system_booking_limits_exceeded_approve',
    'system_booking_trip_create',
    'system_linkedtrip_attrinfo',
    'system_linkedtrip_approveinfo',
    'system_booking_trip_info',
    'system_booking_trip_change',
    'system_booking_trip_ordering',
    'system_booking_trip_limit',
    'system_booking_trip_limit_approved',
    'system_business_trip_file_delivery',
    'system_deadline',
    'system_create_request_sw',
    'system_trip_info_sw',
    'system_item_voucher_sw',
    'system_cancel_request_sw',
  ])
  @IsString()
  @IsNotEmpty()
  action_type:
    | 'upload'
    | 'accept'
    | 'decline'
    | 'decline_sign'
    | 'sign'
    | 'system'
    | 'generate_document_from_template'
    | 'return'
    | 'competency_eval'
    | 'competency_profile'
    | 'competency_fill'
    | 'system_booking_hook'
    | 'system_booking_approve'
    | 'system_booking_limits_exceeded_approve'
    | 'system_booking_trip_create'
    | 'system_linkedtrip_attrinfo'
    | 'system_linkedtrip_approveinfo'
    | 'system_booking_trip_info'
    | 'system_booking_trip_change'
    | 'system_booking_trip_ordering'
    | 'system_booking_trip_limit'
    | 'system_booking_trip_limit_approved'
    | 'system_business_trip_file_delivery'
    | 'system_deadline'
    | 'system_create_request_sw'
    | 'system_trip_info_sw'
    | 'system_item_voucher_sw'
    | 'system_cancel_request_sw';

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  groups: string[];
}

export class UpdateNode {
  @IsUUID()
  @IsNotEmpty()
  node_id: string;

  @ValidateNested({ each: true })
  @Type(() => UpdateNodeRoles)
  @IsArray()
  @IsOptional()
  roles?: UpdateNodeRoles[];

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  manager_type?: 'operational' | 'legal';

  @ValidateNested()
  @Type(() => DeadlineSettings)
  @IsOptional()
  deadline_settings?: DeadlineSettings;
}

export class UpdateNodesRequest {
  @ValidateNested({ each: true })
  @Type(() => UpdateNode)
  @IsArray()
  @IsNotEmpty()
  nodes: UpdateNode[];
}

export class DeleteWorkflowParticipantRequest {
  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  participant_ids: string[];
}

export class AddWorkflowParticipantRequest {
  @IsUUID()
  @IsNotEmpty()
  group_id: string;
}

export class DeleteValidatorRequest {
  @IsUUID()
  @IsNotEmpty()
  validator_id: string;
}

export class UpdateValidatorRequest {
  @IsUUID()
  @IsNotEmpty()
  validator_id: string;

  @IsOptional()
  settings?:
    | MinDateValidatorSettings
    | MaxDateValidatorSettings
    | MinDurationValidatorSettings
    | MaxDurationValidatorSettings
    | DivisibleByValidatorSettings
    | MinHiringDayValidatorSettings
    | ValuesCompareValidatorSettings
    | AbsenceDaysCheckValidatorSettings
    | AvailableDaysCheckValidatorSettings;
}

export class DeleteManagerSubstituteRequest {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;

  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsUUID()
  @IsNotEmpty()
  substitute_id: string;

  @ValidateNested({ each: true })
  @Type(() => DateRange)
  @IsArray()
  @IsOptional()
  dates?: DateRange[];

  @IsIn(['ALL', 'ALL_TEMPORARY', 'ALL_PERMANENT'])
  @IsString()
  @IsOptional()
  scope?: 'ALL' | 'ALL_TEMPORARY' | 'ALL_PERMANENT';
}

export class CreateManagerSubstituteRequest {
  @IsUUID()
  @IsNotEmpty()
  employee_id: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsNotEmpty()
  event_types: string[];

  @IsUUID()
  @IsNotEmpty()
  substitute_id: string;

  @ValidateNested({ each: true })
  @Type(() => DateRange)
  @IsArray()
  @IsOptional()
  dates?: DateRange[];

  @IsBoolean()
  @IsOptional()
  from_absences?: boolean;
}

export class GetManagerSubstitutesListRequest {
  @IsUUID()
  @IsNotEmpty()
  company_id: string;

  @IsString()
  @IsOptional()
  search_employee?: string;

  @IsInt()
  @IsOptional()
  limit?: number;

  @IsInt()
  @IsOptional()
  offset?: number;
}

export class CreateUnitManagerSetting {
  @IsUUID()
  @IsNotEmpty()
  event_type_id: string;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsNotEmpty()
  approve: 'always' | 'direct_only' | 'never';
}

export class UpdateUnitRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsUUID()
  @IsOptional()
  manager_id?: string;

  @ValidateNested({ each: true })
  @Type(() => CreateUnitManagerSetting)
  @IsArray()
  @IsOptional()
  manager_settings?: CreateUnitManagerSetting[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  assistants?: string[];

  @IsBoolean()
  @IsOptional()
  is_separate?: boolean;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsOptional()
  manager_setting_approve?: 'always' | 'direct_only' | 'never';
}

export class CreateUnitRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsUUID()
  @IsOptional()
  manager_id?: string;

  @ValidateNested({ each: true })
  @Type(() => CreateUnitManagerSetting)
  @IsArray()
  @IsOptional()
  manager_settings?: CreateUnitManagerSetting[];

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  assistants?: string[];

  @IsBoolean()
  @IsNotEmpty()
  is_separate: boolean;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsOptional()
  manager_setting_approve?: 'always' | 'direct_only' | 'never';
}

export type EmployeeStatus = string;

export class GetCompanyEmployeesRequestFilters {
  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  ids?: string[];

  @IsString()
  @IsOptional()
  query?: string;

  @IsIn([
    'data_received',
    'documents_verification',
    'unep_application_signing',
    'unep_releasing',
    'unep_release_failed',
    'unep_release_paused',
    'invite_sent',
    'invite_expired',
    'active',
    'dismissed',
    'wait_registration',
  ])
  @IsString()
  @IsOptional()
  status?:
    | 'data_received'
    | 'documents_verification'
    | 'unep_application_signing'
    | 'unep_releasing'
    | 'unep_release_failed'
    | 'unep_release_paused'
    | 'invite_sent'
    | 'invite_expired'
    | 'active'
    | 'dismissed'
    | 'wait_registration';

  @IsString()
  @IsOptional()
  fio?: string;

  @IsString()
  @IsOptional()
  personnel_number?: string;

  @IsString()
  @IsOptional()
  position?: string;

  @IsString({ each: true })
  @IsArray()
  @IsOptional()
  positions?: string[];

  @IsString()
  @IsOptional()
  unit_name?: string;

  @IsUUID()
  @IsOptional()
  unit_id?: string;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  unit_ids?: string[];

  @IsIn(['operational', 'legal'])
  @IsString()
  @IsOptional()
  unit_type?: 'operational' | 'legal';

  @IsBoolean()
  @IsOptional()
  include_unit_managers?: boolean;

  @IsBoolean()
  @IsOptional()
  include_subunits_employees?: boolean;

  @IsBoolean()
  @IsOptional()
  include_companies_from_account?: boolean;

  @IsBoolean()
  @IsOptional()
  include_foreign_managers?: boolean;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  exclude_ids?: string[];

  @IsUUID()
  @IsOptional()
  exclude_employees_by_schedule_id?: string;

  @IsUUID()
  @IsOptional()
  first_employee_id?: string;

  @IsString()
  @IsOptional()
  custom_type?: string;
}

export class GetCompanyEmployeesRequest {
  @ValidateNested()
  @Type(() => Pagination)
  @IsOptional()
  pagination?: Pagination;

  @ValidateNested()
  @Type(() => GetCompanyEmployeesRequestFilters)
  @IsOptional()
  filters?: GetCompanyEmployeesRequestFilters;
}

export class UploadLogoRequest {
  @Type(() => String)
  @IsNotEmpty()
  main: File;

  @Type(() => String)
  @IsOptional()
  compact?: File;
}

export class CreateCompanyGroupRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;

  @IsBoolean()
  @IsNotEmpty()
  all_companies: boolean;
}

export class CheckCompanyGroupCreationRequest {
  @IsString()
  @IsNotEmpty()
  name: string;

  @IsUUID()
  @IsOptional()
  parent_id?: string;
}

export class UpdateCompanyManagerBlocksRequest {
  @IsIn(['operational', 'legal'])
  @IsString()
  @IsNotEmpty()
  manager_type: 'operational' | 'legal';
}

export class ToggleCompanySkipAccountSyncRequest {
  @IsIn([
    'show_closest_task_widget',
    'payslips_enabled',
    'early_employee_event_access_enabled',
    'manager_starts_from_parent_unit',
    'update_managers_in_events',
    'separate_units_enabled',
    'employee_download_pdf_access',
    'import_operational_units',
    'import_legal_units',
    'business_trips_enabled',
    'search_users_enabled',
    'kontur_fallback_enabled',
  ])
  @IsString()
  @IsNotEmpty()
  name:
    | 'show_closest_task_widget'
    | 'payslips_enabled'
    | 'early_employee_event_access_enabled'
    | 'manager_starts_from_parent_unit'
    | 'update_managers_in_events'
    | 'separate_units_enabled'
    | 'employee_download_pdf_access'
    | 'import_operational_units'
    | 'import_legal_units'
    | 'business_trips_enabled'
    | 'search_users_enabled'
    | 'kontur_fallback_enabled';

  @IsBoolean()
  @IsNotEmpty()
  skip: boolean;

  @IsBoolean()
  @IsOptional()
  settings_value?: boolean;
}

export class UpdateCompanySettingsRequest {
  @IsBoolean()
  @IsOptional()
  import_operational_units?: boolean;

  @IsBoolean()
  @IsOptional()
  import_legal_units?: boolean;

  @IsBoolean()
  @IsOptional()
  payslips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  update_managers_in_events?: boolean;

  @IsBoolean()
  @IsOptional()
  manager_starts_from_parent_unit?: boolean;

  @IsBoolean()
  @IsOptional()
  role_model_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  early_employee_event_access_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  separate_units_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  employee_download_pdf_access?: boolean;

  @IsBoolean()
  @IsOptional()
  show_closest_task_widget?: boolean;

  @IsBoolean()
  @IsOptional()
  business_trips_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  search_users_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  kontur_fallback_enabled?: boolean;

  @IsBoolean()
  @IsOptional()
  show_operational_manager?: boolean;

  @IsBoolean()
  @IsOptional()
  show_legal_manager?: boolean;

  @IsBoolean()
  @IsOptional()
  show_functional_manager?: boolean;

  @IsString()
  @IsOptional()
  timezone?: string;

  @IsBoolean()
  @IsOptional()
  import_timezones?: boolean;

  @IsIn(['always', 'direct_only', 'never'])
  @IsString()
  @IsOptional()
  manager_setting_approve?: 'always' | 'direct_only' | 'never';
}

export class UpdateCompanyRoleModelItem {
  @IsInt()
  @IsNotEmpty()
  object_id: number;

  @IsInt()
  @IsNotEmpty()
  employees: number;

  @IsInt()
  @IsNotEmpty()
  managers: number;

  @IsUUID(undefined, { each: true })
  @IsArray()
  @IsOptional()
  company_groups?: string[];
}

export class UpdateCompanyRoleModelRequest {
  @ValidateNested({ each: true })
  @Type(() => UpdateCompanyRoleModelItem)
  @IsArray()
  @IsNotEmpty()
  values: UpdateCompanyRoleModelItem[];
}

export class UpdateCompanyEmployee {
  @IsUUID()
  @IsNotEmpty()
  id: string;

  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  sign_type:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';
}

export class UpdateCompanyRequest {
  @IsIn([
    'kontur',
    'goskey',
    'cryptopro_simple',
    'cryptopro_local',
    'disabled',
    'ukep',
  ])
  @IsString()
  @IsNotEmpty()
  sign_type:
    | 'kontur'
    | 'goskey'
    | 'cryptopro_simple'
    | 'cryptopro_local'
    | 'disabled'
    | 'ukep';

  @IsIn(['web', 'onecvk', 'public'])
  @IsString()
  @IsNotEmpty()
  employee_sign_type_source: 'web' | 'onecvk' | 'public';

  @ValidateNested({ each: true })
  @Type(() => UpdateCompanyEmployee)
  @IsArray()
  @IsNotEmpty()
  employees: UpdateCompanyEmployee[];
}

@injectable()
export class SettingsService {
  constructor(
    private validator: ValidationService,
    private http: HttpService,
    private unmarshaller: UnmarshallerService,
    private query: QueryService,
  ) {}

  async getCompanyPositions({
    params,
    body = {},
  }: {
    params: GetCompanyPositionsParams;
    body?: GetCompanyPositionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyPositionsParams),
      this.validator.validateOrReject(body, GetCompanyPositionsRequest),
    ]);

    let requestPath = '/company/{company_id}/positions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyPositionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getTimezoneEmployees({
    params,
    body = {},
  }: {
    params: GetTimezoneEmployeesParams;
    body?: TimezoneEmployeesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetTimezoneEmployeesParams),
      this.validator.validateOrReject(body, TimezoneEmployeesRequest),
    ]);

    let requestPath = '/company/{company_id}/timezone/employees';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetTimezoneEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      TimezoneEmployeesResponse,
    );

    return response;
  }

  async getCompanyTimezones({ params }: { params: GetCompanyTimezonesParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyTimezonesParams),
    ]);

    let requestPath = '/company/{company_id}/timezones';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyTimezonesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyTimezonesResponse,
    );

    return response;
  }

  async getTimezoneExceptions({
    params,
    body = {},
  }: {
    params: GetTimezoneExceptionsParams;
    body?: TimezoneExceptionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetTimezoneExceptionsParams),
      this.validator.validateOrReject(body, TimezoneExceptionsRequest),
    ]);

    let requestPath = '/company/{company_id}/timezone/exceptions';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetTimezoneExceptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      TimezoneExceptionsResponse,
    );

    return response;
  }

  async getTimezoneFilterOptions({
    query = {},
  }: {
    query?: GetTimezoneFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetTimezoneFilterOptionsQuery),
    ]);

    let requestPath = '/timezone/options';

    const result = await this.http.get(requestPath, query, { withSide: true });

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAvailableCompanyTimezoneFilters({
    params,
  }: {
    params: GetAvailableCompanyTimezoneFiltersParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetAvailableCompanyTimezoneFiltersParams,
      ),
    ]);

    let requestPath =
      '/company/{company_id}/timezone/exceptions/available_filters';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetAvailableCompanyTimezoneFiltersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      FiltersResponse,
    );

    return response;
  }

  async updateEmployeesTimezone({
    body,
  }: {
    body: UpdateEmployeesTimezoneRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, UpdateEmployeesTimezoneRequest),
    ]);

    let requestPath = '/employee/timezone';

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getAccessLevels() {
    let requestPath = '/access-levels';

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetAccessLevelsResponse,
    );

    return response;
  }

  async updateCompanyCeo({
    params,
    body,
  }: {
    params: UpdateCompanyCeoParams;
    body: UpdateCompanyCeoRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanyCeoParams),
      this.validator.validateOrReject(body, UpdateCompanyCeoRequest),
    ]);

    let requestPath = '/company/{company_id}/ceo';

    const paramsKeys = Object.keys(params) as (keyof UpdateCompanyCeoParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanyCeo({ params }: { params: GetCompanyCeoParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyCeoParams),
    ]);

    let requestPath = '/company/{company_id}/ceo';

    const paramsKeys = Object.keys(params) as (keyof GetCompanyCeoParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyCeoResponse,
    );

    return response;
  }

  async setCompanySmevValidationEsia({
    params,
    body,
  }: {
    params: SetCompanySmevValidationEsiaParams;
    body: SmevValidationEsiaRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        SetCompanySmevValidationEsiaParams,
      ),
      this.validator.validateOrReject(body, SmevValidationEsiaRequest),
    ]);

    let requestPath = '/company/{company_id}/smev_validation_esia';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof SetCompanySmevValidationEsiaParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanySmevValidationEsia({
    params,
  }: {
    params: GetCompanySmevValidationEsiaParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetCompanySmevValidationEsiaParams,
      ),
    ]);

    let requestPath = '/company/{company_id}/smev_validation_esia';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanySmevValidationEsiaParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      SmevValidationEsiaResponse,
    );

    return response;
  }

  async setCompanySmevValidationErrors({
    params,
    body,
  }: {
    params: SetCompanySmevValidationErrorsParams;
    body: SmevValidationErrorsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        SetCompanySmevValidationErrorsParams,
      ),
      this.validator.validateOrReject(body, SmevValidationErrorsRequest),
    ]);

    let requestPath = '/company/{company_id}/smev_validation';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof SetCompanySmevValidationErrorsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanySmevValidationErrors({
    params,
  }: {
    params: GetCompanySmevValidationErrorsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetCompanySmevValidationErrorsParams,
      ),
    ]);

    let requestPath = '/company/{company_id}/smev_validation';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanySmevValidationErrorsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      SmevValidationErrorsResponse,
    );

    return response;
  }

  async updateEventType({
    params,
    body = {},
  }: {
    params: UpdateEventTypeParams;
    body?: UpdateEventTypeRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateEventTypeParams),
      this.validator.validateOrReject(body, UpdateEventTypeRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}';

    const paramsKeys = Object.keys(params) as (keyof UpdateEventTypeParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async companyEventTypesCollection({
    params,
    body,
  }: {
    params: CompanyEventTypesCollectionParams;
    body: UpdateCollectionsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        CompanyEventTypesCollectionParams,
      ),
      this.validator.validateOrReject(body, UpdateCollectionsRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/collection';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CompanyEventTypesCollectionParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async companyEventTypesCollectionOptions({
    params,
  }: {
    params: CompanyEventTypesCollectionOptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        CompanyEventTypesCollectionOptionsParams,
      ),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/collection/options';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CompanyEventTypesCollectionOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCollectionOptionsResponse,
    );

    return response;
  }

  async updateCompanyProfilesSettings({
    params,
    body,
  }: {
    params: UpdateCompanyProfilesSettingsParams;
    body: UpdateCompanyProfilesSettings;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        UpdateCompanyProfilesSettingsParams,
      ),
      this.validator.validateOrReject(body, UpdateCompanyProfilesSettings),
    ]);

    let requestPath = '/company/{company_id}/profiles/settings';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateCompanyProfilesSettingsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanyProfilesSettings({
    params,
  }: {
    params: GetCompanyProfilesSettingsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyProfilesSettingsParams),
    ]);

    let requestPath = '/company/{company_id}/profiles/settings';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyProfilesSettingsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyProfilesSettings,
    );

    return response;
  }

  async getProfilesOptions({
    query = {},
  }: {
    query?: GetProfilesOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetProfilesOptionsQuery),
    ]);

    let requestPath = '/company/profiles/options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getProfiles({
    query = {},
  }: {
    query?: GetProfilesQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetProfilesQuery),
    ]);

    let requestPath = '/company/profiles';

    const result = await this.http.get(requestPath, query, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetProfilesResponse,
    );

    return response;
  }

  async deleteUserGroup({ body }: { body: DeleteUserGroupRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, DeleteUserGroupRequest),
    ]);

    let requestPath = '/user/group';

    const result = await this.http.delete(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async addUserGroup({ body }: { body: AddUserGroupRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, AddUserGroupRequest),
    ]);

    let requestPath = '/user/group';

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanyGroupList({ body }: { body: GetCompanyGroupListRequest }) {
    await Promise.all([
      this.validator.validateOrReject(body, GetCompanyGroupListRequest),
    ]);

    let requestPath = '/company/group_list';

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyGroupListResponse,
    );

    return response;
  }

  async getNodeOptions({ params }: { params: GetNodeOptionsParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetNodeOptionsParams),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/node/options';

    const paramsKeys = Object.keys(params) as (keyof GetNodeOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetNodeOptionsResponse,
    );

    return response;
  }

  async updateNodes({
    params,
    body,
  }: {
    params: UpdateNodesParams;
    body: UpdateNodesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateNodesParams),
      this.validator.validateOrReject(body, UpdateNodesRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/node';

    const paramsKeys = Object.keys(params) as (keyof UpdateNodesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getNewWorkflowParticipantOptions({
    params,
  }: {
    params: GetNewWorkflowParticipantOptionsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        GetNewWorkflowParticipantOptionsParams,
      ),
    ]);

    let requestPath =
      '/company/event_types/{event_type_id}/participant/options';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetNewWorkflowParticipantOptionsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetNewWorkflowParticipantOptionsResponse,
    );

    return response;
  }

  async deleteWorkflowParticipant({
    params,
    body,
  }: {
    params: DeleteWorkflowParticipantParams;
    body: DeleteWorkflowParticipantRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteWorkflowParticipantParams),
      this.validator.validateOrReject(body, DeleteWorkflowParticipantRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/participant';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof DeleteWorkflowParticipantParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async addWorkflowParticipant({
    params,
    body,
  }: {
    params: AddWorkflowParticipantParams;
    body: AddWorkflowParticipantRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, AddWorkflowParticipantParams),
      this.validator.validateOrReject(body, AddWorkflowParticipantRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/participant';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof AddWorkflowParticipantParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async deleteValidator({
    params,
    body,
  }: {
    params: DeleteValidatorParams;
    body: DeleteValidatorRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteValidatorParams),
      this.validator.validateOrReject(body, DeleteValidatorRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/validator';

    const paramsKeys = Object.keys(params) as (keyof DeleteValidatorParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async updateValidator({
    params,
    body,
  }: {
    params: UpdateValidatorParams;
    body: UpdateValidatorRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateValidatorParams),
      this.validator.validateOrReject(body, UpdateValidatorRequest),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/validator';

    const paramsKeys = Object.keys(params) as (keyof UpdateValidatorParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getEventTypeWorkflow({
    params,
  }: {
    params: GetEventTypeWorkflowParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetEventTypeWorkflowParams),
    ]);

    let requestPath = '/company/event_types/{event_type_id}/workflow';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetEventTypeWorkflowParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetEventTypeWorkflowResponse,
    );

    return response;
  }

  async getManagerSubstituteAbsenceOptions({
    query,
  }: {
    query: GetManagerSubstituteAbsenceOptionsQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetManagerSubstituteAbsenceOptionsQuery,
      ),
    ]);

    let requestPath = '/company/manager_substitute/absence_options';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      ManagerSubstituteAbsenceOptionsResponse,
    );

    return response;
  }

  async deleteManagerSubstitute({
    body,
  }: {
    body: DeleteManagerSubstituteRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, DeleteManagerSubstituteRequest),
    ]);

    let requestPath = '/company/manager_substitute';

    const result = await this.http.delete(requestPath, body, {
      withSide: true,
    });

    return result;
  }

  async createManagerSubstitute({
    body,
  }: {
    body: CreateManagerSubstituteRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, CreateManagerSubstituteRequest),
    ]);

    let requestPath = '/company/manager_substitute';

    const result = await this.http.post(requestPath, body, { withSide: true });

    return result;
  }

  async getManagerSubstitutes({
    query,
  }: {
    query: GetManagerSubstitutesQuery;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetManagerSubstitutesQuery),
    ]);

    let requestPath = '/company/manager_substitute';

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetManagerSubstitutesResponse,
    );

    return response;
  }

  async getManagerSubstitutesList({
    body,
  }: {
    body: GetManagerSubstitutesListRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(body, GetManagerSubstitutesListRequest),
    ]);

    let requestPath = '/company/manager_substitute/list';

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetManagerSubstitutesListResponse,
    );

    return response;
  }

  async getUnitStaff({
    query = {},
    params,
  }: {
    query?: GetUnitStaffQuery;
    params: GetUnitStaffParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUnitStaffQuery),
      this.validator.validateOrReject(params, GetUnitStaffParams),
    ]);

    let requestPath = '/company/{company_id}/unit/{unit_id}/staff';

    const paramsKeys = Object.keys(params) as (keyof GetUnitStaffParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitStaffResponse,
    );

    return response;
  }

  async deleteUnit({ params }: { params: DeleteUnitParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, DeleteUnitParams),
    ]);

    let requestPath = '/company/{company_id}/unit/{unit_id}';

    const paramsKeys = Object.keys(params) as (keyof DeleteUnitParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.delete(requestPath, {}, {});

    return result;
  }

  async updateUnit({
    params,
    body,
  }: {
    params: UpdateUnitParams;
    body: UpdateUnitRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateUnitParams),
      this.validator.validateOrReject(body, UpdateUnitRequest),
    ]);

    let requestPath = '/company/{company_id}/unit/{unit_id}';

    const paramsKeys = Object.keys(params) as (keyof UpdateUnitParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, {});

    return result;
  }

  async getUnit({ params }: { params: GetUnitParams }) {
    await Promise.all([this.validator.validateOrReject(params, GetUnitParams)]);

    let requestPath = '/company/{company_id}/unit/{unit_id}';

    const paramsKeys = Object.keys(params) as (keyof GetUnitParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitResponse,
    );

    return response;
  }

  async createUnit({
    params,
    body,
  }: {
    params: CreateUnitParams;
    body: CreateUnitRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CreateUnitParams),
      this.validator.validateOrReject(body, CreateUnitRequest),
    ]);

    let requestPath = '/company/{company_id}/unit';

    const paramsKeys = Object.keys(params) as (keyof CreateUnitParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateUnitResponse,
    );

    return response;
  }

  async getUnitChildren({ params }: { params: GetUnitChildrenParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetUnitChildrenParams),
    ]);

    let requestPath = '/company/{company_id}/unit/{unit_id}/children';

    const paramsKeys = Object.keys(params) as (keyof GetUnitChildrenParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitChildrenResponse,
    );

    return response;
  }

  async getUnitChildrenForBatch({
    params,
  }: {
    params: GetUnitChildrenForBatchParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetUnitChildrenForBatchParams),
    ]);

    let requestPath = '/company/{company_id}/batch/unit/{unit_id}/children';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetUnitChildrenForBatchParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitChildrenResponse,
    );

    return response;
  }

  async getUnitsTree({
    query = {},
    params,
  }: {
    query?: GetUnitsTreeQuery;
    params: GetUnitsTreeParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUnitsTreeQuery),
      this.validator.validateOrReject(params, GetUnitsTreeParams),
    ]);

    let requestPath = '/company/{company_id}/unit/tree';

    const paramsKeys = Object.keys(params) as (keyof GetUnitsTreeParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitsTreeResponse,
    );

    return response;
  }

  async getUnitsTreeForBatch({
    query = {},
    params,
  }: {
    query?: GetUnitsTreeForBatchQuery;
    params: GetUnitsTreeForBatchParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUnitsTreeForBatchQuery),
      this.validator.validateOrReject(params, GetUnitsTreeForBatchParams),
    ]);

    let requestPath = '/company/{company_id}/batch/unit/tree';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetUnitsTreeForBatchParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitsTreeResponse,
    );

    return response;
  }

  async getCompaniesFilterOptions() {
    let requestPath = '/companies/options';

    const result = await this.http.get(requestPath, {}, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getSegmentsFilterOptions({
    query = {},
  }: {
    query?: GetSegmentsFilterOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetSegmentsFilterOptionsQuery),
    ]);

    let requestPath = '/segments/options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getAbsenceTypesOptions({
    query = {},
  }: {
    query?: GetAbsenceTypesOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetAbsenceTypesOptionsQuery),
    ]);

    let requestPath = '/absences/options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => AbsenceFilterOption)
      response: AbsenceFilterOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getUnitsOptions({
    query = {},
  }: {
    query?: GetUnitsOptionsQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetUnitsOptionsQuery),
    ]);

    let requestPath = '/units/options';

    const result = await this.http.get(requestPath, query, {});

    class Response {
      @ValidateNested()
      @Type(() => FilterSelectOption)
      response: FilterSelectOption[];
    }
    const { response } = await this.unmarshaller.unmarshall(
      { response: result },
      Response,
    );

    return response;
  }

  async getUnitTypes({ params }: { params: GetUnitTypesParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetUnitTypesParams),
    ]);

    let requestPath = '/company/{company_id}/unit/types';

    const paramsKeys = Object.keys(params) as (keyof GetUnitTypesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetUnitTypesResponse,
    );

    return response;
  }

  async getCompanySubstituteEmployees({
    query = {},
    params,
  }: {
    query?: GetCompanySubstituteEmployeesQuery;
    params: GetCompanySubstituteEmployeesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCompanySubstituteEmployeesQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetCompanySubstituteEmployeesParams,
      ),
    ]);

    let requestPath =
      '/company/{company_id}/substitute_employees/{employee_id}';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanySubstituteEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyEmployeesResponse,
    );

    return response;
  }

  async getCompanyEmployeesWithoutRoleModel({
    query,
    params,
  }: {
    query: GetCompanyEmployeesWithoutRoleModelQuery;
    params: GetCompanyEmployeesWithoutRoleModelParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        query,
        GetCompanyEmployeesWithoutRoleModelQuery,
      ),
      this.validator.validateOrReject(
        params,
        GetCompanyEmployeesWithoutRoleModelParams,
      ),
    ]);

    let requestPath = '/company/{company_id}/employees/substitute_options';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyEmployeesWithoutRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyEmployeesSubstituteOptionsResponse,
    );

    return response;
  }

  async getCompanyEmployees({
    params,
    body = {},
  }: {
    params: GetCompanyEmployeesParams;
    body?: GetCompanyEmployeesRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyEmployeesParams),
      this.validator.validateOrReject(body, GetCompanyEmployeesRequest),
    ]);

    let requestPath = '/company/{company_id}/employees';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyEmployeesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, {
      withSide: true,
      reloadIfOldVersion: true,
    });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyEmployeesResponse,
    );

    return response;
  }

  async getCompanyEventTypes({
    query = {},
    params,
  }: {
    query?: GetCompanyEventTypesQuery;
    params: GetCompanyEventTypesParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCompanyEventTypesQuery),
      this.validator.validateOrReject(params, GetCompanyEventTypesParams),
    ]);

    let requestPath = '/company/{company_id}/event_types';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyEventTypesParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyEventTypesResponse,
    );

    return response;
  }

  async getCompanyGroupUsers({
    params,
  }: {
    params: GetCompanyGroupUsersParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyGroupUsersParams),
    ]);

    let requestPath = '/company/{company_id}/group_users';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyGroupUsersParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyGroupUsers,
    );

    return response;
  }

  async getCompanyLogo({
    query = {},
  }: {
    query?: GetCompanyLogoQuery;
  } = {}) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCompanyLogoQuery),
    ]);

    let requestPath = '/company/logo';

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: true,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async getCompanyLogoById({
    query = {},
    params,
  }: {
    query?: GetCompanyLogoByIdQuery;
    params: GetCompanyLogoByIdParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCompanyLogoByIdQuery),
      this.validator.validateOrReject(params, GetCompanyLogoByIdParams),
    ]);

    let requestPath = '/company/{company_id}/logo';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyLogoByIdParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.getFile(
      requestPath,
      {
        withAuth: true,
        withSide: true,
        isJson: true,
      },
      query,
    );

    return result;
  }

  async updateCompanyLogo({
    params,
    body,
  }: {
    params: UpdateCompanyLogoParams;
    body: UploadLogoRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanyLogoParams),
      this.validator.validateOrReject(body, UploadLogoRequest),
    ]);

    let requestPath = '/company/{company_id}/logo/upload';

    const paramsKeys = Object.keys(params) as (keyof UpdateCompanyLogoParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { isJson: false });

    return result;
  }

  async getCompanyEventsStat({
    params,
  }: {
    params: GetCompanyEventsStatParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyEventsStatParams),
    ]);

    let requestPath = '/company/{company_id}/events_stat';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyEventsStatParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyEventsStat,
    );

    return response;
  }

  async createCompanyGroup({
    params,
    body,
  }: {
    params: CreateCompanyGroupParams;
    body: CreateCompanyGroupRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CreateCompanyGroupParams),
      this.validator.validateOrReject(body, CreateCompanyGroupRequest),
    ]);

    let requestPath = '/company/{company_id}/groups';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CreateCompanyGroupParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CreateCompanyGroupResponse,
    );

    return response;
  }

  async getCompanyGroups({
    query = {},
    params,
  }: {
    query?: GetCompanyGroupsQuery;
    params: GetCompanyGroupsParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(query, GetCompanyGroupsQuery),
      this.validator.validateOrReject(params, GetCompanyGroupsParams),
    ]);

    let requestPath = '/company/{company_id}/groups';

    const paramsKeys = Object.keys(params) as (keyof GetCompanyGroupsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, query, { withSide: true });

    const response = await this.unmarshaller.unmarshall(result, Groups);

    return response;
  }

  async checkCompanyGroupCreation({
    params,
    body,
  }: {
    params: CheckCompanyGroupCreationParams;
    body: CheckCompanyGroupCreationRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, CheckCompanyGroupCreationParams),
      this.validator.validateOrReject(body, CheckCompanyGroupCreationRequest),
    ]);

    let requestPath = '/company/{company_id}/groups/check';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof CheckCompanyGroupCreationParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.post(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      CheckCompanyGroupCreationResponse,
    );

    return response;
  }

  async updateCompanyManagerBlocks({
    params,
    body,
  }: {
    params: UpdateCompanyManagerBlocksParams;
    body: UpdateCompanyManagerBlocksRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanyManagerBlocksParams),
      this.validator.validateOrReject(body, UpdateCompanyManagerBlocksRequest),
    ]);

    let requestPath = '/company/{company_id}/manager_block';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateCompanyManagerBlocksParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      UpdateCompanyManagerBlocksResponse,
    );

    return response;
  }

  async toggleCompanySkipAccountSync({
    params,
    body,
  }: {
    params: ToggleCompanySkipAccountSyncParams;
    body: ToggleCompanySkipAccountSyncRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(
        params,
        ToggleCompanySkipAccountSyncParams,
      ),
      this.validator.validateOrReject(
        body,
        ToggleCompanySkipAccountSyncRequest,
      ),
    ]);

    let requestPath = '/company/{company_id}/settings/skip_account_sync';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof ToggleCompanySkipAccountSyncParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      EditableCompany,
    );

    return response;
  }

  async updateCompanySettings({
    params,
    body = {},
  }: {
    params: UpdateCompanySettingsParams;
    body?: UpdateCompanySettingsRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanySettingsParams),
      this.validator.validateOrReject(body, UpdateCompanySettingsRequest),
    ]);

    let requestPath = '/company/{company_id}/settings';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateCompanySettingsParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getCompaniesForOrgStructure() {
    let requestPath = '/company/org_structure/list';

    const result = await this.http.get(
      requestPath,
      {},
      { reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      OrgStructureCompanyListResponse,
    );

    return response;
  }

  async getCompaniesFromAccount({
    params,
  }: {
    params: GetCompaniesFromAccountParams;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompaniesFromAccountParams),
    ]);

    let requestPath = '/company/{company_id}/by_account';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompaniesFromAccountParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyListResponse,
    );

    return response;
  }

  async updateCompanyRoleModel({
    params,
    body,
  }: {
    params: UpdateCompanyRoleModelParams;
    body: UpdateCompanyRoleModelRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanyRoleModelParams),
      this.validator.validateOrReject(body, UpdateCompanyRoleModelRequest),
    ]);

    let requestPath = '/company/{company_id}/role_model';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof UpdateCompanyRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, { withSide: true });

    return result;
  }

  async getCompanyRoleModel({ params }: { params: GetCompanyRoleModelParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyRoleModelParams),
    ]);

    let requestPath = '/company/{company_id}/role_model';

    const paramsKeys = Object.keys(
      params,
    ) as (keyof GetCompanyRoleModelParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, { withSide: true });

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyRoleModelResponse,
    );

    return response;
  }

  async updateCompany({
    params,
    body,
  }: {
    params: UpdateCompanyParams;
    body: UpdateCompanyRequest;
  }) {
    await Promise.all([
      this.validator.validateOrReject(params, UpdateCompanyParams),
      this.validator.validateOrReject(body, UpdateCompanyRequest),
    ]);

    let requestPath = '/company/{company_id}';

    const paramsKeys = Object.keys(params) as (keyof UpdateCompanyParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.put(requestPath, body, {});

    return result;
  }

  async getCompany({ params }: { params: GetCompanyParams }) {
    await Promise.all([
      this.validator.validateOrReject(params, GetCompanyParams),
    ]);

    let requestPath = '/company/{company_id}';

    const paramsKeys = Object.keys(params) as (keyof GetCompanyParams)[];
    if (paramsKeys.length) {
      paramsKeys.forEach((param) => {
        requestPath = requestPath.replace(
          `{${param}}`,
          encodeURIComponent(String(params[param])),
        );
      });
    }

    const result = await this.http.get(requestPath, {}, {});

    const response = await this.unmarshaller.unmarshall(
      result,
      GetCompanyResponse,
    );

    return response;
  }

  async getCompaniesFindByGroupAdmin() {
    let requestPath = '/company/list/find_by_group_admin';

    const result = await this.http.get(
      requestPath,
      {},
      { withSide: true, reloadIfOldVersion: true },
    );

    const response = await this.unmarshaller.unmarshall(
      result,
      CompanyListResponse,
    );

    return response;
  }
}
